import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { CellContext } from "@tanstack/react-table"
import styled from "styled-components"

import { clientApi } from "store/api/clientApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { UITypes } from "types"

import useCustomerEditModal from "hooks/useCustomerEditModal"

const Container = styled.div<{ $isClickable: boolean }>`
  text-decoration: ${({ $isClickable }) => ($isClickable ? "underline" : "none")};
  color: ${({ $isClickable }) => ($isClickable ? "#2db2c8" : "#0f172a")};
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};

  &:hover {
    opacity: ${({ $isClickable }) => ($isClickable ? "0.8" : "1")};
  }
`

interface CustomerEmailProps {
  cell: CellContext<UITypes.TableBodyRow, UITypes.TableBodyCell>
}

export const CustomerEmail = ({ cell }: CustomerEmailProps) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { onShowEditCustomerModal, renderEditCustomerModal } = useCustomerEditModal()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const customerEmail = cell.getValue().value as string
  const rowItem = cell.row.original.rowSettings.item
  // const rowSettings = cell.row.original.rowSettings
  const customerId: number | null = rowItem.client_id || null
  const deviceSN: string | undefined = rowItem.sn
  const devicesCount: number | undefined = rowItem.devices_count
  const hasPhysicalStatus = "phisical_status" in (rowItem || {})
  const isInstalled = rowItem.phisical_status === "installed"
  const [isLoading, setIsLoading] = useState(false)

  const hasEmail = !!customerEmail
  const isClickable = hasEmail && !!(deviceSN || (devicesCount !== undefined && devicesCount > 0))

  const navigateToCustomer = (clientId: number) => {
    if (userProfile?.type === 11) {
      onShowEditCustomerModal(clientId)
    } else {
      const baseUrl = `/customers/${clientId}/overview`
      const url = deviceSN ? `${baseUrl}?selectedDevice=${deviceSN}` : baseUrl
      navigate(url)
    }
  }

  const handleCustomerClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    if (!isClickable || isLoading) return

    if (customerId) {
      navigateToCustomer(customerId)
      return
    }

    if (!customerEmail) return
    setIsLoading(true)
    try {
      const result = await dispatch(clientApi.endpoints.getClients.initiate({ customer_email: customerEmail })).unwrap()
      const client = result.data.find(c => c.email === customerEmail)
      if (client) {
        navigateToCustomer(client.id)
      }
    } catch {
    } finally {
      setIsLoading(false)
    }
  }

  const getCustomerEmail = () => (devicesCount !== undefined || !hasPhysicalStatus || isInstalled ? customerEmail : "")

  return (
    <>
      <Container $isClickable={isClickable} onClickCapture={isClickable ? handleCustomerClick : undefined}>
        {getCustomerEmail() || "-"}
      </Container>
      {renderEditCustomerModal()}
    </>
  )
}
